import type { CourseContentVersion, CourseVideoContent, VideoProgressResult } from "./types.ts";

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

export function buildPrivacyEnhancedYouTubeUrl(videoId: string): string {
  if (!YOUTUBE_ID.test(videoId)) throw new Error("Identificador de vídeo no válido.");
  return `https://www.youtube-nocookie.com/embed/${videoId}?enablejsapi=1&playsinline=1&rel=0`;
}

export function calculateVideoProgress(watchedSeconds: number, durationSeconds: number, transcriptAlternativeCompleted = false): VideoProgressResult {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) throw new Error("La duración del vídeo debe ser positiva.");
  const percent = Math.max(0, Math.min(100, Math.floor(watchedSeconds / durationSeconds * 100)));
  const milestones = ([25, 50, 75, 90, 100] as const).filter((milestone) => percent >= milestone);
  if (transcriptAlternativeCompleted) return { percent, milestones, completed: true, completionMode: "transcript_alternative" };
  return { percent, milestones, completed: percent >= 90, completionMode: percent >= 90 ? "playback" : "pending" };
}

function validateVideo(video: CourseVideoContent): void {
  if (!video.transcript.trim()) throw new Error("Todo vídeo publicado necesita transcripción.");
  if (!video.captionsAvailable) throw new Error("Todo vídeo publicado necesita subtítulos.");
  if (video.provider === "youtube_unlisted") {
    if (!video.videoId || !YOUTUBE_ID.test(video.videoId) || video.privateObjectRef) throw new Error("Configuración de YouTube no válida.");
  } else if (!video.privateObjectRef || video.videoId) throw new Error("Configuración de vídeo privado no válida.");
}

export function validateCourseContentVersion(content: CourseContentVersion): void {
  if (!content.id || !content.moduleId || content.version < 1) throw new Error("Versión de contenido incompleta.");
  if (!content.blocks.length) throw new Error("El contenido necesita al menos un bloque.");
  for (const block of content.blocks) {
    if (!block.id || !["heading", "paragraph", "list", "callout", "activity"].includes(block.type)) throw new Error("Bloque de contenido no permitido.");
    if (block.type === "list" ? !block.items?.length : !block.text?.trim()) throw new Error(`El bloque ${block.id} está vacío.`);
  }
  if (content.video) validateVideo(content.video);
  if (content.status === "published" && !content.publishedAt) throw new Error("Una versión publicada necesita fecha estable.");
}
